import { BaseState } from "./States/BaseState";
import { GameStateEvents, GameStates } from "./Consts";
import { PreEventState } from "./States/PreEventState";
import { WarmUpState } from "./States/WarmUpState";
import { PreRaceState } from "./States/PreRaceState";
import { RaceInProgressState } from "./States/RaceInProgressState";
import { PostRaceState } from "./States/PostRaceState";
import { PostEventState } from "./States/PostEventState";
import { EndEventState } from "./States/EndEventState";
import { ISubscribableEvent } from "./ISubscribableEvent";

///This class is a singleton that various game states.
class GameStateManager implements ISubscribableEvent {
  private static instance: GameStateManager | null = null;

  // Private constructor to prevent external instantiation
  private constructor() {
    this.preEventGameState = new PreEventState(this);
    this.warmUpState = new WarmUpState(this);
    this.preRaceState = new PreRaceState(this);
    this.raceInProgressState = new RaceInProgressState(this);
    this.postRaceState = new PostRaceState(this);
    this.postEventState = new PostEventState(this);
    this.endEventState = new EndEventState(this);
  }

  // Method to create or get the singleton instance
  public static getInstance(): GameStateManager {
    if (GameStateManager.instance === null) {
      GameStateManager.instance = new GameStateManager();
    }
    return GameStateManager.instance;
  }

  private currentState: BaseState<GameStateManager> | null = null;

  // Various game states
  //Flow: preEvent->WarmUp->PreRace->RaceInProgress->PostRace->PostEvent->EndEvent
  public preEventGameState: BaseState<GameStateManager>;
  public warmUpState: BaseState<GameStateManager>;
  public preRaceState: BaseState<GameStateManager>;
  public raceInProgressState: BaseState<GameStateManager>;
  public postRaceState: BaseState<GameStateManager>;
  public postEventState: BaseState<GameStateManager>;
  public endEventState: BaseState<GameStateManager>;

  // Event listener arrays for each state
  private onEnterPreEventGameStateListeners: (() => void)[] = [];
  private onEnterWarmUpStateListeners: (() => void)[] = [];
  private onEnterPreRaceStateListeners: (() => void)[] = [];
  private onEnterRaceInProgressStateListeners: (() => void)[] = [];
  private onEnterPostRaceStateListeners: (() => void)[] = [];
  private onEnterPostEventStateListeners: (() => void)[] = [];
  private onEnterEndEventStateListeners: (() => void)[] = [];

  public changeState(newState: BaseState<GameStateManager>) {
    if (this.currentState) {
      this.currentState.onExit();
    }

    this.currentState = newState;
    this.currentState.onEnter(this); // Pass the StateManager reference to the state.

    // Invoke the event listeners for the current state
    // this.invokeEventListenersForState(newState); //NOTE : Could run event listeners here instead of in the state onEnter method?
  }

  public getStateByName(stateName: string): BaseState<GameStateManager> {
    let stateToReturn: BaseState<GameStateManager>;
    switch (stateName) {
      case GameStates.PRE_EVENT_STATE:
        stateToReturn = this.preEventGameState;
        break;
      case GameStates.WARM_UP_STATE:
        stateToReturn = this.warmUpState;
        break;
      case GameStates.PRE_RACE_STATE:
        stateToReturn = this.preRaceState;
        break;
      case GameStates.RACE_IN_PROGRESS_STATE:
        stateToReturn = this.raceInProgressState;
        break;
      case GameStates.POST_RACE_STATE:
        stateToReturn = this.postRaceState;
        break;
      case GameStates.POST_EVENT_STATE:
        stateToReturn = this.postEventState;
        break;
      case GameStates.END_EVENT_STATE:
        stateToReturn = this.endEventState;
        break;
      default:
        throw new Error(`Invalid state name: ${stateName}`);
    }

    return stateToReturn;
  }

  public getCurrentState(): BaseState<GameStateManager> | null {
    return this.currentState;
  }

  /// Invoke the event listeners for the given state
  private invokeEventListenersForState(state: BaseState<GameStateManager>) {
    const listeners = this.getEventListeners(state);

    for (const listener of listeners) {
      listener();
    }
  }

  private getEventListeners(
    state: BaseState<GameStateManager>,
  ): (() => void)[] {
    switch (state) {
      case this.preEventGameState:
        return this.onEnterPreEventGameStateListeners;
      case this.warmUpState:
        return this.onEnterWarmUpStateListeners;
      case this.preRaceState:
        return this.onEnterPreRaceStateListeners;
      case this.raceInProgressState:
        return this.onEnterRaceInProgressStateListeners;
      case this.postRaceState:
        return this.onEnterPostRaceStateListeners;
      case this.postEventState:
        return this.onEnterPostEventStateListeners;
      case this.endEventState:
        return this.onEnterEndEventStateListeners;
      default:
        return [];
    }
  }

  public subscribeToEvent(eventName: string, listener: () => void): void {
    // Ensure that eventName is one of the allowed events from EulerStateEvents
    if (!Object.values(GameStateEvents).includes(eventName)) {
      throw new Error(`Invalid event name: ${eventName}`);
    }
    const listeners = this.getListenersArray(eventName);
    if (listeners && !listeners.includes(listener)) {
      listeners.push(listener);
    }
  }

  public unsubscribeFromEvent(eventName: string, listener: () => void): void {
    // Ensure that eventName is one of the allowed events from EulerStateEvents
    if (!Object.values(GameStateEvents).includes(eventName)) {
      throw new Error(`Invalid event name: ${eventName}`);
    }
    const listeners = this.getListenersArray(eventName);
    if (listeners) {
      const index = listeners.indexOf(listener);
      if (index !== -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private getListenersArray(eventName: string): (() => void)[] {
    switch (eventName) {
      case GameStateEvents.ON_ENTER_PRE_EVENT_GAME_STATE:
        return this.onEnterPreEventGameStateListeners;

      case GameStateEvents.ON_ENTER_WARM_UP_STATE:
        return this.onEnterWarmUpStateListeners;

      case GameStateEvents.ON_ENTER_PRE_RACE_STATE:
        return this.onEnterPreRaceStateListeners;

      case GameStateEvents.ON_ENTER_RACE_IN_PROGRESS_STATE:
        return this.onEnterRaceInProgressStateListeners;

      case GameStateEvents.ON_ENTER_POST_RACE_STATE:
        return this.onEnterPostRaceStateListeners;

      case GameStateEvents.ON_ENTER_POST_EVENT_STATE:
        return this.onEnterPostEventStateListeners;

      case GameStateEvents.ON_ENTER_END_EVENT_STATE:
        return this.onEnterEndEventStateListeners;

      default:
        return [];
    }
  }

  /*
   * Functions to invoke the event listeners for each state
   */
  public enteredPreEventGameState() {
    if (this.onEnterPreEventGameStateListeners) {
      this.onEnterPreEventGameStateListeners.forEach((listener) => listener());
    }
  }

  public enteredWarmUpState() {
    if (this.onEnterWarmUpStateListeners) {
      this.onEnterWarmUpStateListeners.forEach((listener) => listener());
    }
  }

  public enteredPreRaceState() {
    if (this.onEnterPreRaceStateListeners) {
      this.onEnterPreRaceStateListeners.forEach((listener) => listener());
    }
  }

  public enteredRaceInProgressState() {
    if (this.onEnterRaceInProgressStateListeners) {
      this.onEnterRaceInProgressStateListeners.forEach((listener) =>
        listener(),
      );
    }
  }

  public enteredPostRaceState() {
    if (this.onEnterPostRaceStateListeners) {
      this.onEnterPostRaceStateListeners.forEach((listener) => listener());
    }
  }

  public enteredPostEventState() {
    if (this.onEnterPostEventStateListeners) {
      this.onEnterPostEventStateListeners.forEach((listener) => listener());
    }
  }

  public enteredEndEventState() {
    if (this.onEnterEndEventStateListeners) {
      this.onEnterEndEventStateListeners.forEach((listener) => listener());
    }
  }
}

export { GameStateManager as StateManager };
